// src/hooks/useLogger.js
// Централизованный логгер для фронтенда
// Перехватывает console.error, window.onerror, unhandledrejection
// и отправляет всё на /api/clientlog

const API = process.env.REACT_APP_API_URL || "";
const IS_DEV = process.env.NODE_ENV === "development";

// Буфер — не спамим сервер одинаковыми ошибками
const _sent = new Set();
const _queue = [];
const MAX_QUEUE = 50;
let _flushing = false;

function _isExtensionSource(value) {
  const text = String(value || "").toLowerCase();
  return text.startsWith("chrome-extension://")
    || text.startsWith("moz-extension://")
    || text.startsWith("safari-web-extension://")
    || text.includes("chrome-extension://")
    || text.includes("moz-extension://")
    || text.includes("safari-web-extension://");
}

function _ignoreGlobalError(message, source, stack) {
  const msg = String(message || "").trim();
  if (msg === "Script error." && !source) return true;
  if (_isExtensionSource(source) || _isExtensionSource(stack)) return true;
  return false;
}

async function _flush() {
  if (_flushing || _queue.length === 0) return;
  _flushing = true;
  while (_queue.length > 0) {
    const entry = _queue.shift();
    try {
      await fetch(`${API}/api/clientlog`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(entry),
        keepalive: true,
      });
    } catch (_) {
      // Если сеть упала — не ломаем приложение
    }
  }
  _flushing = false;
}

function _send(level, message, context = {}) {
  const key = `${level}:${message}`;
  if (_sent.has(key)) return; // дедупликация
  _sent.add(key);
  setTimeout(() => _sent.delete(key), 60_000); // сбрасываем через минуту

  const entry = {
    level,
    message: String(message).slice(0, 500),
    context: {
      ...context,
      url: window.location.pathname,
      ts: new Date().toISOString(),
    },
  };

  if (IS_DEV) {
    // В дев режиме просто console
    console[level === "error" ? "error" : "warn"]("[LOG]", entry);
    return;
  }

  if (_queue.length >= MAX_QUEUE) _queue.shift();
  _queue.push(entry);
  _flush();
}

// ── Публичный API ─────────────────────────────────────────────────────────────

export const log = {
  info:  (msg, ctx) => _send("info",  msg, ctx),
  warn:  (msg, ctx) => _send("warning", msg, ctx),
  error: (msg, ctx) => _send("error", msg, ctx),

  // Удобные хелперы для игр
  game: (event, roomId, gameType, extra = {}) =>
    _send("info", `[GAME] ${event}`, { roomId, gameType, ...extra }),

  gameError: (event, roomId, gameType, err, extra = {}) =>
    _send("error", `[GAME ERROR] ${event}: ${err?.message || err}`, {
      roomId, gameType, stack: err?.stack?.slice(0, 300), ...extra
    }),
};

// ── Глобальный перехват ───────────────────────────────────────────────────────

export function initGlobalErrorCapture() {
  if (IS_DEV) return; // в дев режиме не нужно

  // JS ошибки
  const origOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (_ignoreGlobalError(message, source, error?.stack)) {
      return origOnError ? origOnError(message, source, lineno, colno, error) : false;
    }
    _send("error", String(message).slice(0, 300), {
      source: String(source).split("/").slice(-2).join("/"),
      line: lineno,
      col: colno,
      stack: error?.stack?.slice(0, 300),
    });
    return origOnError ? origOnError(message, source, lineno, colno, error) : false;
  };

  // Promise rejections
  window.addEventListener("unhandledrejection", (e) => {
    const msg = e.reason?.message || String(e.reason) || "Unhandled rejection";
    if (_ignoreGlobalError(msg, "", e.reason?.stack)) return;
    _send("error", msg.slice(0, 300), {
      stack: e.reason?.stack?.slice(0, 300),
    });
  });

  // React error boundary helper — вызывай из componentDidCatch
  window.__neonLogReactError = (error, info) => {
    _send("error", `[REACT] ${error?.message || error}`, {
      componentStack: info?.componentStack?.slice(0, 300),
      stack: error?.stack?.slice(0, 300),
    });
  };
}